import React, { useState } from 'react';
import { 
  Award, Download, Copy, Check, 
  AlertCircle, ArrowLeft, Sparkles, FileText, HelpCircle, ExternalLink 
} from 'lucide-react';
import { LocalModelToggle } from './LocalModelToggle';

interface IpGuardProps {
  language: 'uk' | 'en' | 'pl';
  onBack: () => void;
  onGoHome?: () => void;
  useEnterpriseContext?: boolean;
  enterpriseName?: string;
}

const LOADING_STEPS_UK = [
  "Ініціалізація аналітичного модуля інтелектуальної власності...",
  "Патентний аналітик перевіряє унікальність назви та об'єкта...",
  "Технічний письменник формує опис та реферат...",
  "Спеціаліст з діловодства заповнює заяви та розраховує збори...",
  "Генерація фінального пакету для УКРНОІВІ..."
];

const LOADING_STEPS_EN = [
  "Initializing intellectual property AI agents...",
  "Patent analyst performs trademark uniqueness conflict checks...",
  "Technical writer builds object descriptions and user guides...",
  "IP filing specialist completes templates and calculates fees...",
  "Generating final registration packet for UANIPIO..."
];

export default function IpGuard({ language, onBack, onGoHome, useEnterpriseContext, enterpriseName }: IpGuardProps) {
  const isUk = language === 'uk';
  const [ipType, setIpType] = useState(isUk ? "Комп'ютерна програма (авторське право)" : "Computer Software (Copyright)");
  const [objectName, setObjectName] = useState('');
  const [objectDescription, setObjectDescription] = useState(
    useEnterpriseContext ? (isUk ? 'Опис об\'єкта та дані про правовласника будуть автоматично доповнені з бази знань кабінету.' : 'Object description and applicant details will be automatically loaded from the workspace knowledge base.') : ''
  );
  
  const [useLocalModel, setUseLocalModel] = useState<boolean>(() => {
    const saved = localStorage.getItem('prefer_local_model');
    return saved !== null ? saved === 'true' : true;
  });

  const [isAuditing, setIsAuditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<string | null>(null);
  
  const [copiedReport, setCopiedReport] = useState(false);
  const [loadingStepIndex, setLoadingStepIndex] = useState(0);

  React.useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isAuditing) {
      setLoadingStepIndex(0);
      const steps = isUk ? LOADING_STEPS_UK : LOADING_STEPS_EN;
      interval = setInterval(() => {
        setLoadingStepIndex((prev) => (prev < steps.length - 1 ? prev + 1 : prev));
      }, 7000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isAuditing, isUk]);

  const handleRun = async () => {
    if (!objectName.trim() || !objectDescription.trim()) return;

    setIsAuditing(true);
    setError(null);
    setReport(null);

    try {
      const customKey = localStorage.getItem('custom_gemini_api_key');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (customKey) {
        headers['x-api-key'] = customKey;
      }

      const response = await fetch('/api/run-templated-crew', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          crewName: "Реєстрація авторського права та ТМ",
          agentId: "ip-guard",
          useLocalModel,
          inputs: {
            ip_type: ipType,
            object_name: objectName,
            object_description: objectDescription
          },
          useEnterpriseContext
        })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || (isUk ? 'Помилка генерації документів для реєстрації.' : 'Failed to generate registration documents.'));
      }

            const data = await response.json();
      if (data.jobId) {
        const pollJob = async () => {
          try {
            const statusRes = await fetch(`/api/crew-status/${data.jobId}`);
            if (!statusRes.ok) {
              throw new Error(isUk ? 'Помилка перевірки статусу завдання.' : 'Status check failed.');
            }
            const statusData = await statusRes.json();
            if (statusData.status === 'completed') {
              const rawReport = statusData.report || statusData.result || '';
              const resText = typeof rawReport === 'string'
                ? rawReport
                : (typeof rawReport === 'object' && rawReport !== null
                    ? (rawReport.report || rawReport.result || JSON.stringify(rawReport, null, 2))
                    : String(rawReport));

              if (!resText) {
                throw new Error(isUk ? 'Сервер повернув порожній результат звіту.' : 'Server returned empty report content.');
              }
              setReport(resText);
              setIsAuditing(false);
            } else if (statusData.status === 'failed') {
              setError(statusData.error || (isUk ? 'Помилка генерації звіту.' : 'Report generation failed.'));
              setIsAuditing(false);
            } else {
              setTimeout(pollJob, 2000);
            }
          } catch (pollErr: any) {
            console.error(pollErr);
            setError(pollErr.message || (isUk ? 'Помилка опитування завдання.' : 'Polling error.'));
            setIsAuditing(false);
          }
        };
        setTimeout(pollJob, 1000);
      } else {
        setReport(data.report || data.result || '');
        setIsAuditing(false);
      }
    } catch (err: any) {
      console.error(err);
      setError(err.message || (isUk ? 'Сталася помилка.' : 'An error occurred.'));
      setIsAuditing(false);
    }
  };

  const handleDownloadWord = async () => {
    if (!report) return;

    try {
      const name = "IP_Registration_" + objectName.replace(/[^a-zA-Z0-9А-Яа-яЄєІіЇїҐґ_-]/g, "_");
      const response = await fetch('/api/generate-docx', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: report,
          fileName: name
        })
      });

      if (!response.ok) throw new Error(isUk ? 'Не вдалося згенерувати Word файл.' : 'Failed to generate Word file.');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${name}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error(err);
      alert(err.message);
    }
  };

  const handleCopyReport = () => {
    if (!report) return;
    navigator.clipboard.writeText(report);
    setCopiedReport(true);
    setTimeout(() => setCopiedReport(false), 2000);
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto px-4 pb-12 text-slate-800">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-200 pb-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <button
              onClick={onGoHome || onBack}
              className="px-3.5 py-2 text-xs font-mono font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded transition-all cursor-pointer uppercase flex items-center gap-1.5"
            >
              🏠 {isUk ? "На головну" : "Home"}
            </button>
            <button
              onClick={onBack}
              className="px-3.5 py-2 text-xs font-mono font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded transition-all cursor-pointer uppercase flex items-center gap-1.5"
            >
              <ArrowLeft className="w-4 h-4 text-blue-600" />
              <span>{isUk ? "← До Бізнес Центру" : "← Back to Business Hub"}</span>
            </button>
          </div>
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Award className="w-6 h-6 text-purple-500" />
              <span>{isUk ? "Реєстрація ТМ та авторського права" : "AI IP & Trademark Guard"}</span>
            </h1>
            <p className="text-xs text-slate-500">
              {isUk ? "Попередній аналіз унікальності, опис об'єкта та підготовка заяв та звітів для УКРНОІВІ" : "Trademark uniqueness audits, object descriptions, and application forms drafting"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <LocalModelToggle
            isLocal={useLocalModel}
            onToggle={(enabled) => {
              setUseLocalModel(enabled);
              localStorage.setItem('prefer_local_model', enabled ? 'true' : 'false');
            }}
            language={language}
          />
        </div>
      </div>

      {useEnterpriseContext && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3.5 flex items-center justify-between text-xs text-amber-800">
          <div className="flex items-center gap-2.5">
            <Sparkles className="w-4 h-4 text-amber-600 animate-pulse" />
            <div>
              <span className="font-bold">{isUk ? "Активовано контекст підприємства: " : "Enterprise Context Active: "}</span>
              <span className="font-mono">{enterpriseName || (isUk ? "База знань компанії" : "Company Knowledge Base")}</span>
            </div>
          </div>
          <span className="text-[10px] px-2 py-0.5 bg-amber-500/20 rounded text-amber-700 font-bold uppercase tracking-wider">
            {isUk ? "База знань" : "Knowledge Base"}
          </span>
        </div>
      )}

      {/* Main Form */}
      {!report && !isAuditing && (
        <div className="space-y-4">
          <button
            type="button"
            onClick={() => {
              setIpType(isUk ? "Торговельна марка (ТМ)" : "Trademark (TM)");
              setObjectName("AgroChain AI");
              setObjectDescription("Платформа відстеження походження сільгосппродукції на базі блокчейн та ШІ. Класи МКТП: 09 (ПЗ), 35 (Брокерські послуги), 42 (IT-сервіси). Автори-розробники: Гриценко О.М., Коваленко П.Т. Права належать ТОВ 'АгроЧейн Технолоджіс'.");
              setError(null);
            }}
            className="w-full py-2.5 px-4 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-xl text-emerald-800 font-mono text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-2 shadow-2xs"
          >
            <Sparkles className="w-4 h-4 text-emerald-600 animate-pulse" />
            <span>{isUk ? "⚡ Спробувати на демо-прикладі в 1 клік (Реєстрація ТМ 'AgroChain AI')" : "⚡ Try 1-click Demo Sample (Trademark Registration)"}</span>
          </button>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-4">
            <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4 shadow-xs">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                    {isUk ? "Тип об'єкта ІВ" : "Intellectual Property Type"}
                  </label>
                  <select
                    value={ipType}
                    onChange={(e) => setIpType(e.target.value)}
                    className="w-full p-2.5 bg-slate-550/10 border border-slate-200 rounded-lg focus:border-purple-500 focus:outline-none text-sm transition-colors text-slate-850"
                  >
                    <option value={isUk ? "Комп'ютерна програма (авторське право)" : "Computer Software (Copyright)"}>{isUk ? "Комп'ютерна програма (авторське право)" : "Computer Software (Copyright)"}</option>
                    <option value={isUk ? "Торговельна марка (ТМ)" : "Trademark (TM)"}>{isUk ? "Торговельна марка (ТМ)" : "Trademark (TM)"}</option>
                    <option value={isUk ? "Винахід / Корисна модель" : "Invention / Utility Model"}>{isUk ? "Винахід / Корисна модель" : "Invention / Utility Model"}</option>
                    <option value={isUk ? "Твір літератури / Наукова праця" : "Literary / Scientific Work"}>{isUk ? "Твір літератури / Наукова праця" : "Literary / Scientific Work"}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                    {isUk ? "Назва об'єкта" : "Object Name"}
                  </label>
                  <input
                    type="text"
                    placeholder={isUk ? "Наприклад: Комп'ютерна програма «DocuMind»" : "Example: Computer Program 'DocuMind'"}
                    value={objectName}
                    onChange={(e) => setObjectName(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:border-purple-500 focus:outline-none text-sm transition-colors text-slate-850"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                  {isUk ? "Опис об'єкта (функціонал, технології, особливості)" : "Object description (features, technologies, specifications)"}
                </label>
                <textarea
                  placeholder={isUk 
                    ? "Опишіть технічні особливості, алгоритми, мову програмування, призначення продукту для патентування..." 
                    : "Describe technical features, algorithms, programming languages, and the product's destination..."}
                  value={objectDescription}
                  onChange={(e) => setObjectDescription(e.target.value)}
                  rows={6}
                  className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl focus:border-purple-500 focus:ring-1 focus:ring-purple-500/30 text-sm focus:outline-none transition-colors placeholder-slate-400 leading-relaxed font-mono"
                />
              </div>
            </div>

            <button
              onClick={handleRun}
              disabled={!objectName.trim() || !objectDescription.trim()}
              className="w-full py-3 bg-purple-500 hover:bg-purple-655 disabled:bg-slate-100 disabled:text-slate-400 text-white font-black text-sm uppercase rounded-xl tracking-wider flex items-center justify-center gap-2 transition-colors cursor-pointer shadow-xs"
            >
              <Award className="w-4 h-4" />
              <span>{isUk ? "Підготувати пакет документів" : "Draft Registration Packet"}</span>
            </button>

            <div className="mt-4 space-y-2 text-[11px] text-slate-500 font-medium leading-relaxed bg-slate-50 border border-slate-200/60 rounded-xl p-3.5">
              <div className="flex items-start gap-2">
                <FileText className="w-3.5 h-3.5 text-purple-500 mt-0.5 shrink-0" />
                <span>
                  {isUk ? (
                    <>
                      Подати документи можна поштою або в електронній формі через офіційну систему{" "}
                      <a
                        href="https://sis.uaniipio.org"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-purple-600 hover:text-purple-800 font-bold underline inline-flex items-center gap-0.5"
                      >
                        СЕВ ЗЕС <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    </>
                  ) : (
                    <>
                      Documents can be submitted by post or electronically via the official{" "}
                      <a
                        href="https://sis.uaniipio.org"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-purple-600 hover:text-purple-800 font-bold underline inline-flex items-center gap-0.5"
                      >
                        SIS UANIPIO <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    </>
                  )}
                </span>
              </div>
              <div className="flex items-start gap-2 border-t border-slate-200/50 pt-2.5">
                <HelpCircle className="w-3.5 h-3.5 text-purple-500 mt-0.5 shrink-0" />
                <span>
                  {isUk ? (
                    <>
                      Реквізити для сплати зборів та правила оформлення доступні на офіційному сайті{" "}
                      <a
                        href="https://nipo.gov.ua"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-purple-600 hover:text-purple-800 font-bold underline inline-flex items-center gap-0.5"
                      >
                        УКРНОІВІ <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    </>
                  ) : (
                    <>
                      Payment details and filing regulations are available on the official website of{" "}
                      <a
                        href="https://nipo.gov.ua"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-purple-600 hover:text-purple-800 font-bold underline inline-flex items-center gap-0.5"
                      >
                        UANIPIO <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    </>
                  )}
                </span>
              </div>
            </div>
          </div>

          {/* Guidelines Sidebar */}
          <div className="space-y-4">
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 space-y-4">
              <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5 border-b border-slate-200 pb-2">
                <HelpCircle className="w-4 h-4 text-purple-500" />
                <span>{isUk ? "Як це працює?" : "How it works?"}</span>
              </h3>
              <ul className="space-y-3 font-mono text-[10px] text-slate-650 leading-relaxed">
                <li className="flex items-start gap-2">
                  <span className="w-4 h-4 bg-purple-100 text-purple-650 font-bold rounded-full flex items-center justify-center shrink-0">1</span>
                  <span>{isUk ? "Патентний аналітик перевіряє назву на схожість з зареєстрованими ТМ." : "Patent Analyst verifies name exclusivity against active registers."}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-4 h-4 bg-purple-100 text-purple-650 font-bold rounded-full flex items-center justify-center shrink-0">2</span>
                  <span>{isUk ? "Технічний письменник розробляє опис та реферат за регламентами." : "Technical Writer structures abstract and usage descriptions."}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-4 h-4 bg-purple-100 text-purple-650 font-bold rounded-full flex items-center justify-center shrink-0">3</span>
                  <span>{isUk ? "Діловод формує заяви, рахує суму збору та супровідний лист." : "Filing Agent outputs official forms, sheets, and calculates fees."}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
        </div>
      )}

      {/* Loading State */}
      {isAuditing && (
        <div className="bg-white border border-slate-200 rounded-xl p-8 shadow-xs flex flex-col items-center justify-center min-h-[350px] space-y-6">
          <div className="relative w-16 h-16 flex items-center justify-center">
            <div className="absolute w-full h-full border-4 border-purple-200 rounded-full animate-pulse" />
            <div className="absolute w-full h-full border-4 border-t-purple-500 rounded-full animate-spin" />
            <Award className="w-6 h-6 text-purple-500" />
          </div>
          <div className="text-center space-y-2 max-w-md">
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-800">
              {isUk ? "Підготовка пакету документів аналітичними модулями..." : "Drafting IP documents by AI..."}
            </h3>
            <p className="text-xs text-slate-500 font-mono italic leading-normal px-4">
              {isUk ? LOADING_STEPS_UK[loadingStepIndex] : LOADING_STEPS_EN[loadingStepIndex]}
            </p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-5 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <h4 className="text-xs font-bold text-red-800 uppercase tracking-wider">
              {isUk ? "Помилка аналізу" : "Execution Error"}
            </h4>
            <p className="text-xs text-red-655 leading-relaxed font-mono">{error}</p>
          </div>
        </div>
      )}

      {/* Report View */}
      {report && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-xs overflow-hidden">
          {/* Action Bar */}
          <div className="bg-slate-50 border-b border-slate-200 px-5 py-4 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <div className="bg-purple-50 p-1.5 rounded border border-purple-100">
                <FileText className="w-4 h-4 text-purple-650" />
              </div>
              <span className="text-xs font-bold text-slate-800 font-mono uppercase tracking-wider">
                {isUk ? "Пакет документів для реєстрації" : "IP Registration Packet"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={handleCopyReport}
                className="py-1.5 px-3 bg-white hover:bg-slate-550/30 text-slate-650 hover:text-slate-800 border border-slate-200 rounded-lg text-[10px] font-bold font-mono uppercase tracking-wider flex items-center gap-1 cursor-pointer transition-colors shadow-xs"
              >
                {copiedReport ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                <span>{copiedReport ? (isUk ? 'Скопійовано' : 'Copied') : (isUk ? 'Копіювати' : 'Copy')}</span>
              </button>
              <button 
                onClick={handleDownloadWord}
                className="py-1.5 px-3 bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white rounded-lg text-[10px] font-bold font-mono uppercase tracking-wider flex items-center gap-1 cursor-pointer transition-colors shadow-xs"
              >
                <Download className="w-3.5 h-3.5" />
                <span>Word (.docx)</span>
              </button>
            </div>
          </div>

          {/* Document Content */}
          <div className="p-6 sm:p-8 prose prose-slate max-w-none text-xs leading-relaxed font-mono bg-slate-50 border-b border-slate-100 overflow-x-auto whitespace-pre-wrap text-slate-800">
            {report}
          </div>
          
          <div className="p-4 bg-white flex justify-end">
            <button
              onClick={() => {
                setReport(null);
                setObjectName('');
                setObjectDescription('');
              }}
              className="py-2 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold font-mono text-[10px] uppercase rounded-lg tracking-wider cursor-pointer transition-colors"
            >
              {isUk ? "Створити нову заяву" : "New Application"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
